import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser'

/**
 * Microsoft sign-in, ported from Wong/Cerebro: a PUBLIC client with PKCE —
 * no client secret exists anywhere — whose ID token is the bearer the
 * Herald GET APIs validate. Norn JWT remains the only caller of POST.
 *
 * Mode comes from the build: VITE_AUTH_MODE=entra turns this on; anything
 * else (dev default) makes the provider a no-op so fixtures work offline.
 */

const MODE = import.meta.env.VITE_AUTH_MODE === 'entra' ? 'entra' : 'none'
const TENANT = import.meta.env.VITE_ENTRA_TENANT_ID ?? ''
const CLIENT = import.meta.env.VITE_ENTRA_CLIENT_ID ?? ''
const SCOPES = ['openid', 'profile', 'email'] as const

let bearer: string | null = null
export const getBearerToken = () => bearer
export const authMode = MODE

let msalInstance: PublicClientApplication | null = null
let redirectPromise: Promise<AuthenticationResult | null> | null = null

async function getMsal(): Promise<PublicClientApplication> {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: CLIENT,
        authority: `https://login.microsoftonline.com/${TENANT}`,
        redirectUri: `${window.location.origin}/login`,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      },
      cache: { cacheLocation: 'localStorage' },
    })
    await msalInstance.initialize()
  }
  return msalInstance
}

let redirectError: string | null = null

function handleRedirectOnce(instance: PublicClientApplication) {
  if (!redirectPromise) {
    redirectPromise = instance.handleRedirectPromise().catch((err) => {
      console.error('MSAL redirect failed', err)
      redirectError = err instanceof Error ? err.message : String(err)
      return null
    })
  }
  return redirectPromise
}

type AuthValue = {
  ready: boolean
  authenticated: boolean
  account: AccountInfo | null
  authError: string | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue>({
  ready: true,
  authenticated: true,
  account: null,
  authError: null,
  signIn: async () => {},
  signOut: async () => {},
})

export const useAuth = () => useContext(AuthContext)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(MODE === 'none')
  const [authenticated, setAuthenticated] = useState(MODE === 'none')
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)

  const apply = useCallback((result: AuthenticationResult | null) => {
    bearer = result?.idToken ?? result?.accessToken ?? null
    setAccount(result?.account ?? null)
    setAuthenticated(bearer !== null)
  }, [])

  useEffect(() => {
    if (MODE === 'none') return
    let cancelled = false
    ;(async () => {
      try {
        const instance = await getMsal()
        const redirect = await handleRedirectOnce(instance)
        if (cancelled) return
        if (redirect?.idToken || redirect?.accessToken) {
          apply(redirect)
          if (window.location.pathname === '/login') {
            window.history.replaceState(null, '', '/messages')
          }
        } else {
          const existing = instance.getAllAccounts()[0]
          if (existing) {
            try {
              apply(await instance.acquireTokenSilent({ account: existing, scopes: [...SCOPES] }))
            } catch (err) {
              if (!(err instanceof InteractionRequiredAuthError)) {
                console.error('MSAL silent sign-in failed', err)
                if (!cancelled) setAuthError(err instanceof Error ? err.message : String(err))
              }
            }
          }
        }
      } finally {
        if (!cancelled) {
          if (redirectError) setAuthError(redirectError)
          setReady(true)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apply])

  useEffect(() => {
    if (MODE === 'none' || !authenticated) return
    const timer = setInterval(async () => {
      const instance = await getMsal()
      const current = account ?? instance.getAllAccounts()[0]
      if (!current) return
      try {
        apply(
          await instance.acquireTokenSilent({
            account: current,
            scopes: [...SCOPES],
            forceRefresh: true,
          }),
        )
      } catch {
        /* next GET 401 sends them to sign in again */
      }
    }, 45 * 60 * 1000)
    return () => clearInterval(timer)
  }, [authenticated, account, apply])

  const signIn = useCallback(async () => {
    try {
      setAuthError(null)
      const instance = await getMsal()
      await instance.loginRedirect({ scopes: [...SCOPES] })
    } catch (err) {
      console.error('MSAL sign-in failed', err)
      setAuthError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  const signOut = useCallback(async () => {
    bearer = null
    setAuthenticated(false)
    setAccount(null)
    const instance = await getMsal()
    await instance.logoutRedirect()
  }, [])

  return (
    <AuthContext.Provider value={{ ready, authenticated, account, authError, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
